import { useState } from 'react';
import { Plus, X, Check, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore, fmtDate, daysUntil } from '../lib/store';
import { Reveal, EmptyState, Field, inputCls, EASE } from '../components/Bits';

const PREP_GROUPS = ['Persiapan Awal', 'Akad & Dokumen', 'Resepsi & Keluarga', 'Menuju Hari H'];
const CAT_GROUP = {
  WO: 'Akad & Dokumen',
  Venue: 'Persiapan Awal',
  Catering: 'Resepsi & Keluarga',
  Dekorasi: 'Resepsi & Keluarga',
  'Foto & Video': 'Resepsi & Keluarga',
  MUA: 'Resepsi & Keluarga',
  Busana: 'Resepsi & Keluarga',
  Entertainment: 'Resepsi & Keluarga',
  Lainnya: 'Persiapan Awal',
};

function TaskRow({ t, toggleTask }) {
  const days = daysUntil(t.targetDate);
  return (
    <div data-testid={`task-row-${t.id}`} className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors ${t.done ? 'border-[#CBDCCE] bg-[#EBF2EC]/40' : 'border-line bg-white'}`}>
      <button
        data-testid={`task-toggle-${t.id}`}
        onClick={() => toggleTask(t.id)}
        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${t.done ? 'bg-sage border-sage' : 'border-ink/25 bg-white hover:border-terra'}`}
      >
        {t.done && <Check size={12} strokeWidth={3.5} className="text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${t.done ? 'text-slate-400 line-through' : 'text-pewter'}`}>{t.title}</p>
        <p className="font-mono text-[10px] text-slate-400 mt-0.5">
          {t.category} · {fmtDate(t.targetDate)}{!t.done && isFinite(days) && days >= 0 ? ` · H-${days}` : ''}
        </p>
      </div>
    </div>
  );
}

export default function Prep() {
  const { state, addTask, toggleTask } = useStore();
  const [showForm, setShowForm] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [f, setF] = useState({ title: '', category: 'WO', targetDate: '', vendorId: '' });

  const open = state.tasks.filter((t) => !t.done).sort((a, b) => (a.targetDate < b.targetDate ? -1 : 1));
  const now = open.filter((t) => daysUntil(t.targetDate) <= 14).slice(0, 3);
  const done = state.tasks.filter((t) => t.done);
  const groupOf = (t) => (daysUntil(t.targetDate) <= 14 ? 'Menuju Hari H' : CAT_GROUP[t.category] || 'Persiapan Awal');

  const submit = (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.targetDate) return;
    addTask({ title: f.title.trim(), category: f.category, targetDate: f.targetDate, vendorId: f.vendorId });
    setF({ title: '', category: 'WO', targetDate: '', vendorId: '' });
    setShowForm(false);
  };

  return (
    <div data-testid="prep-page" className="space-y-6">
      <Reveal>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-medium tracking-tight text-ink">Persiapan</h1>
            <p className="mt-1.5 font-serif italic text-sm text-cashmere">Sedikit demi sedikit, yang penting terus bergerak.</p>
          </div>
          <button data-testid="add-task-btn" onClick={() => setShowForm((s) => !s)} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-ink text-ivory px-4 py-2.5 text-xs font-bold hover:bg-inkdeep transition-colors">
            {showForm ? <X size={14} /> : <Plus size={14} />} Tambah
          </button>
        </div>
      </Reveal>

      {open.length > 0 && (
        <Reveal delay={0.05}>
          <section data-testid="prep-focus" className="relative overflow-hidden rounded-[20px] border border-line bg-white soft-shadow p-5">
            <div className="kawung absolute inset-0 opacity-[0.03] pointer-events-none" />
            <div className="relative">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra mb-2">Fokus Minggu Ini</p>
              <p className="font-serif text-lg font-medium text-ink">{(now.length ? now : open).length} hal perlu dibereskan</p>
              <ul className="mt-2 space-y-1">
                {(now.length ? now : open).slice(0, 2).map((t) => (
                  <li key={t.id} className="text-sm text-pewter flex items-baseline gap-2">
                    <span className="w-1 h-1 rounded-full bg-terra shrink-0 translate-y-[-2px]" /> {t.title}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[11px] text-cashmere">Nggak harus semuanya sekaligus. Bereskan yang paling dekat dulu.</p>
            </div>
          </section>
        </Reveal>
      )}

      {showForm && (
        <form data-testid="add-task-form" onSubmit={submit} className="rounded-2xl border border-ink/10 bg-white p-5 space-y-4">
          <Field label="Persiapan">
            <input data-testid="task-title-input" className={inputCls} placeholder="Jadwalkan fitting busana" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required />
          </Field>
          <Field label="Kategori">
            <select data-testid="task-category-select" className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {['WO', 'Venue', 'Catering', 'Dekorasi', 'Foto & Video', 'MUA', 'Busana', 'Entertainment', 'Lainnya'].map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Target tanggal">
            <input data-testid="task-date-input" type="date" className={inputCls} value={f.targetDate} onChange={(e) => setF({ ...f, targetDate: e.target.value })} required />
          </Field>
          <Field label="Vendor terkait" optional>
            <select data-testid="task-vendor-select" className={inputCls} value={f.vendorId} onChange={(e) => setF({ ...f, vendorId: e.target.value })}>
              <option value="">Tidak ada</option>
              {state.vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Field>
          <button data-testid="task-save-btn" type="submit" className="w-full rounded-full bg-ink text-ivory py-3.5 text-sm font-bold">Simpan Persiapan</button>
        </form>
      )}

      {open.length === 0 && done.length === 0 ? (
        <EmptyState title="Belum ada persiapan" desc="Mulai dari hal kecil yang paling dekat dengan hari H. Sebelum sah, jangan asal pasrah. 😄" />
      ) : (
        <>
          <section>
            <div className="space-y-6">
              {PREP_GROUPS.map((g) => {
                const items = open.filter((t) => groupOf(t) === g);
                if (items.length === 0) return null;
                return (
                  <div key={g} data-testid={`prep-group-${g.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra mb-3">
                      {g} <span className="text-cashmere/70">· {items.length}</span>
                    </p>
                    <div className="space-y-2">{items.map((t) => <TaskRow key={t.id} t={t} toggleTask={toggleTask} />)}</div>
                  </div>
                );
              })}
            </div>
          </section>

          {done.length > 0 && (
            <section>
              <button data-testid="prep-done-toggle" onClick={() => setShowDone((s) => !s)} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-3">
                Selesai ({done.length}) <ChevronDown size={13} className={`transition-transform ${showDone ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showDone && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: EASE }} className="overflow-hidden">
                    <div className="space-y-2">{done.map((t) => <TaskRow key={t.id} t={t} toggleTask={toggleTask} />)}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </section>
          )}
        </>
      )}
    </div>
  );
}
